import React, { useState, useEffect, useRef } from "react";
import {
    Box, TextField, Button, MenuItem, Typography,
    Chip, CircularProgress, Divider, IconButton, Tooltip,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import { useTablePagination, useAppMeta } from "hooks";
import { exportExcelFile } from "utils/exportExcelFile";
import { useLazyGetVendorLeadNoteListQuery } from "store/services/noteApi";
import { localizeDateTimeShort } from "utils/dateFormats";

const RESPONDED_OPTIONS = [
    { label: "All", value: "" },
    { label: "Responded", value: "true" },
    { label: "Not Responded", value: "false" },
];

const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "Vendor Lead Note List" },
];

const stripHtml = (html) => html?.replace(/<[^>]*>/g, "") ?? "";

const VendorLeadNoteListReport = () => {
    useAppMeta("Vendor Lead Note List");

    const navigate = useNavigate();
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const [keyword, setKeyword] = useState("");
    const [debouncedKeyword, setDebouncedKeyword] = useState("");
    const [createdAtStart, setCreatedAtStart] = useState("");
    const [createdAtEnd, setCreatedAtEnd] = useState("");
    const [updatedAtStart, setUpdatedAtStart] = useState("");
    const [updatedAtEnd, setUpdatedAtEnd] = useState("");
    const [responded, setResponded] = useState("");

    const keywordTimerRef = useRef(null);

    const [fetchList, { data: listData, isLoading, isFetching }] =
        useLazyGetVendorLeadNoteListQuery();

    const [fetchExport] = useLazyGetVendorLeadNoteListQuery();

    const buildParams = (extra = {}) => ({
        page: currentPage,
        limit: perPage,
        ...(debouncedKeyword && { keyword: debouncedKeyword }),
        ...(createdAtStart && { createdAtStart }),
        ...(createdAtEnd && { createdAtEnd }),
        ...(updatedAtStart && { updatedAtStart }),
        ...(updatedAtEnd && { updatedAtEnd }),
        ...(responded !== "" && { responded }),
        ...extra,
    });

    useEffect(() => {
        fetchList(buildParams());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentPage,
        perPage,
        debouncedKeyword,
        createdAtStart,
        createdAtEnd,
        updatedAtStart,
        updatedAtEnd,
        responded,
    ]);

    const handleKeywordChange = (e) => {
        const val = e.target.value;
        setKeyword(val);
        clearTimeout(keywordTimerRef.current);
        keywordTimerRef.current = setTimeout(() => setDebouncedKeyword(val), 400);
    };

    const handleClearFilters = () => {
        setKeyword("");
        setDebouncedKeyword("");
        setCreatedAtStart("");
        setCreatedAtEnd("");
        setUpdatedAtStart("");
        setUpdatedAtEnd("");
        setResponded("");
    };

    const handleExport = async () => {
        try {
            const res = await fetchExport(
                buildParams({ page: undefined, limit: undefined })
            ).unwrap();
            const exportData = res?.data?.map((row) => ({
                "Lead ID": row.vendorLead?.leadId ?? "N/A",
                "Type": row.type ?? "N/A",
                "Content": stripHtml(row.content),
                "Responded": row.responded ? "Yes" : "No",
                "Created By": row.createdBy?.name ?? "N/A",
                "Updated By": row.updatedBy?.name ?? "N/A",
                "Created At": row.createdAt ? localizeDateTimeShort(row.createdAt) : "N/A",
                "Updated At": row.updatedAt ? localizeDateTimeShort(row.updatedAt) : "N/A",
            }));
            exportExcelFile({
                sheetData: exportData,
                fileName: `Vendor_Lead_Notes_${createdAtStart || "All"}_to_${createdAtEnd || "All"}`,
            });
        } catch (err) {
            console.error("Export failed:", err);
        }
    };

    const columns = [
        {
            name: "Lead ID",
            selector: (row) => row?.vendorLead?.leadId,
            cell: (row) => (
                <div className="font-mono text-xs">{row?.vendorLead?.leadId ?? "N/A"}</div>
            ),
            minWidth: "140px",
        },
        {
            name: "Type",
            selector: (row) => row?.type,
            cell: (row) => <div>{row?.type ?? "N/A"}</div>,
            minWidth: "100px",
        },
        {
            name: "Content",
            selector: (row) => row?.content,
            cell: (row) => (
                <div className="line-clamp-2 text-xs">{stripHtml(row?.content)}</div>
            ),
            minWidth: "200px",
        },
        {
            name: "Responded",
            selector: (row) => row?.responded,
            cell: (row) => (
                <Chip
                    label={row?.responded ? "Yes" : "No"}
                    size="small"
                    color={row?.responded ? "success" : "warning"}
                    sx={{ fontSize: "0.7rem", height: 20 }}
                />
            ),
            minWidth: "110px",
        },
        {
            name: "Created By",
            selector: (row) => row?.createdBy?.name,
            cell: (row) => <div className="line-clamp-1">{row?.createdBy?.name ?? "N/A"}</div>,
            minWidth: "130px",
        },
        {
            name: "Updated By",
            selector: (row) => row?.updatedBy?.name,
            cell: (row) => <div className="line-clamp-1">{row?.updatedBy?.name ?? "N/A"}</div>,
            minWidth: "130px",
        },
        {
            name: "Created At",
            selector: (row) => row?.createdAt,
            cell: (row) => (
                <div className="line-clamp-1 text-xs">
                    {row?.createdAt ? localizeDateTimeShort(row.createdAt) : "N/A"}
                </div>
            ),
            minWidth: "160px",
        },
        {
            name: "Updated At",
            selector: (row) => row?.updatedAt,
            cell: (row) => (
                <div className="line-clamp-1 text-xs">
                    {row?.updatedAt ? localizeDateTimeShort(row.updatedAt) : "N/A"}
                </div>
            ),
            minWidth: "160px",
        },
    ];

    const hasFilters = !!(
        keyword || createdAtStart || createdAtEnd ||
        updatedAtStart || updatedAtEnd || responded
    );

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Paper>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 2,
                        pt: 2,
                        pb: 1,
                        gap: 2,
                    }}
                >
                    {/* Stat card – left side, no external margin */}
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "primary.lighter",
                            borderLeft: "4px solid",
                            borderRight: "4px solid",
                            borderColor: "primary.main",
                            display: "inline-block",
                            width: "fit-content",
                            flexShrink: 0,
                            // removed m:2 – wrapper handles spacing
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                            Total Entries
                        </Typography>
                        {isLoading || isFetching ? (
                            <CircularProgress size={24} />
                        ) : (
                            <Typography variant="h3" fontWeight="400" color="primary.dark">
                                {listData?.totalSize ?? 0}
                            </Typography>
                        )}
                    </Box>

                    {/* Filters – right side */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "center",
                            flexWrap: "wrap",
                            // justifyContent: "flex-end" can stay, but space-between handles right alignment
                            flexShrink: 0,
                        }}
                    >
                        <TextField
                            select
                            label="Responded"
                            value={responded}
                            onChange={(e) => setResponded(e.target.value)}
                            size="small"
                            sx={{ minWidth: 150 }}
                        >
                            {RESPONDED_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Created From"
                            type="date"
                            value={createdAtStart}
                            onChange={(e) => setCreatedAtStart(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                        <TextField
                            label="Created To"
                            type="date"
                            value={createdAtEnd}
                            onChange={(e) => setCreatedAtEnd(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                        <TextField
                            label="Updated From"
                            type="date"
                            value={updatedAtStart}
                            onChange={(e) => setUpdatedAtStart(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                        <TextField
                            label="Updated To"
                            type="date"
                            value={updatedAtEnd}
                            onChange={(e) => setUpdatedAtEnd(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClearFilters}
                            disabled={!hasFilters}
                        >
                            Clear
                        </Button>

                        <TextField
                            label="Search Lead ID"
                            value={keyword}
                            onChange={handleKeywordChange}
                            size="small"
                            sx={{ minWidth: 180 }}
                        />

                        <Tooltip title="Export to Excel">
                            <span>
                                <IconButton
                                    color="primary"
                                    onClick={handleExport}
                                    disabled={isLoading || isFetching || !listData?.data?.length}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "primary.main",
                                        borderRadius: 1,
                                        p: "6px",
                                        "&.Mui-disabled": { borderColor: "action.disabled" },
                                    }}
                                >
                                    <Download fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>



                <Divider />

                <AppTable
                    columns={columns}
                    data={listData?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={listData?.totalSize ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onRowClicked={(row) => {
                        if (row?.vendorLead?._id) {
                            navigate(`/app/crm/vendor-lead/${row.vendorLead._id}/detail?tab=note`);
                        }
                    }}
                    pointerOnHover
                    highlightOnHover
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default VendorLeadNoteListReport;